/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React, { useMemo } from 'react';
import { BladeBox } from './BladeBox';
import { useBlades } from './hooks';
import { ResizableBlade } from './ResizableBlade';
import { BladeRegistration, BladeSettings } from './types';
import { getOpenBlades, getOptions, getRegistry } from './utils';

/**
 * Props for the "Blades" component (a box of blades).
 */
export interface BladesProps<T = unknown> {
    /**
     * The blade components registry.
     */
    registrations?: Array<BladeRegistration<T>>;

    /**
     * The global settings applied to all open blades (static defaults).
     */
    settings?: BladeSettings & T;
}

export const Blades: React.FC<BladesProps> = ({
    registrations = [],
    settings = {},
}) => {
    const {
        BladeContainer = ResizableBlade,
        BladeHost = BladeBox,
        fixedWidth,
        resizable,
        scrollIntoViewDelay,
        ...globals
    } = settings;
    const registry = useMemo(() => getRegistry(registrations), [registrations]);
    const initialBlades = useMemo(() => getOpenBlades(registry), [registry]);
    const context = useBlades(initialBlades);
    const { blades = [] } = context;

    const renderChildren = blades.map(openBlade => {
        const { name, options, overrides, ...bladeActions } = openBlade;

        function handleActivation(): void {
            if (!all.active) {
                if (all.collapsed) {
                    bladeActions.onRestore();
                } else {
                    bladeActions.onActivate();
                }
            }
        }

        const reg = registry[name];
        const Component = reg && reg.component;
        const defaults = reg && reg.defaults;
        const all = getOptions(defaults, options, overrides);

        return (
            Component && (
                <BladeContainer
                    key={name}
                    {...bladeActions}
                    resizable={resizable}
                    initialWidth={fixedWidth}
                    scrollIntoViewDelay={scrollIntoViewDelay}
                    {...all}
                    onActivate={handleActivation}
                >
                    <Component
                        key={name}
                        options={{
                            ...globals,
                            ...all,
                        }}
                        context={context}
                        onClose={bladeActions.onClose}
                    />
                </BladeContainer>
            )
        );
    });

    const open = renderChildren.length > 0;
    const closeAll = () => blades.forEach(b => b.onClose());

    return (
        <BladeHost open={open} close={closeAll} context={context}>
            {renderChildren}
        </BladeHost>
    );
};
